import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { setSEO } from '../utils/seo';
import './Home.css';

function Counter({ target, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        let start = 0;
        const step = Math.ceil(target / 60);
        const timer = setInterval(() => {
          start += step;
          if (start >= target) { setCount(target); clearInterval(timer); }
          else setCount(start);
        }, 24);
        obs.disconnect();
      }
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);
  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.12 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Particles() {
  return (
    <div className="particles" aria-hidden="true">
      {[...Array(20)].map((_, i) => (
        <div key={i} className="particle" style={{
          left: `${Math.random() * 100}%`,
          animationDuration: `${8 + Math.random() * 12}s`,
          animationDelay: `${Math.random() * 8}s`,
          width: `${2 + Math.random() * 3}px`,
          height: `${2 + Math.random() * 3}px`,
          opacity: 0.2 + Math.random() * 0.4
        }} />
      ))}
    </div>
  );
}

export default function Home() {
  useEffect(() => { setSEO({ title: 'MS Discoveries – Chatbot CRM, Email Marketing, SEO & Digital Growth Platform India', description: 'MS Discoveries provides Chatbot CRM (MS Chat), Email Campaign, Email Verifier, SEO & AEO, Web Development, iOS & Android Apps, and Data Services for businesses in India.', canonical: '/' }); }, []);
  const [productsRef, productsVis] = useReveal();
  const [servicesRef, servicesVis] = useReveal();
  const [whyRef, whyVis] = useReveal();
  const [statsRef, statsVis] = useReveal();
  const [audienceRef, audienceVis] = useReveal();
  const [ctaRef, ctaVis] = useReveal();

  const products = [
    {
      id: 'ms-chat',
      name: 'MS Chat',
      tagline: 'Chatbot + CRM',
      desc: 'Capture leads from your website using a smart chatbot. Manage customers inside a simple CRM and never miss a lead again.',
      link: 'https://mschat.msdiscoveries.com/',
      external: true,
    },
    {
      id: 'email-campaign',
      name: 'Email Campaign',
      tagline: 'Email Marketing Platform',
      desc: 'Send professional email campaigns to thousands of customers with high deliverability and powerful analytics.',
      link: 'https://mark.msdiscoveries.com/',
      external: true,
    },
    {
      id: 'email-verifier',
      name: 'Email Verifier',
      tagline: 'Email Verification Tool',
      desc: 'Verify email addresses in bulk, reduce bounce rates, and keep your mailing list clean and deliverable.',
      link: 'https://emailverifier.msdiscoveries.com/login',
      external: true,
    },
  ];

  const services = [
    { title: 'SEO & AEO', desc: 'Search Engine Optimization and Answer Engine Optimization to rank higher and get found by AI search tools.' },
    { title: 'Web Development', desc: 'Custom websites and web applications built to convert visitors into customers.' },
    { title: 'iOS & Android Apps', desc: 'Native and cross-platform mobile apps for your business needs.' },
    { title: 'Data Services', desc: 'Quality data sets and data selling solutions for businesses that need accurate, targeted information.' },
    { title: 'Email Marketing', desc: 'Full-service email campaign management, strategy, and execution.' },
    { title: 'Automation', desc: 'Workflow automation using chatbots, WhatsApp, and multi-channel communication tools.' },
  ];

  const whyItems = [
    { title: 'Easy to Use', desc: 'Designed for non-technical business owners. Get started in minutes.' },
    { title: 'Smart Automation', desc: 'Powerful automation tools that work 24/7 without manual effort.' },
    { title: 'Affordable Plans', desc: 'Startup-friendly pricing that scales as your business grows.' },
    { title: 'Lead Management', desc: 'Never lose a lead again. Track, manage and convert from one dashboard.' },
    { title: 'All-in-One', desc: 'Chatbot, email & WhatsApp — everything under one unified platform.' },
    { title: '24/7 Support', desc: 'Round-the-clock customer communication and dedicated support team.' },
  ];

  const audiences = [
    { label: 'Digital Marketing Agencies' },
    { label: 'Real Estate Businesses' },
    { label: 'Educational Institutions' },
    { label: 'Online Stores' },
    { label: 'Service-Based Businesses' },
    { label: 'Small & Growing Companies' },
  ];

  return (
    <div className="home">
      {/* HERO */}
      <section className="hero">
        <Particles />
        <div className="hero__bg-grid" aria-hidden="true" />
        <div className="hero__glow" aria-hidden="true" />
        <div className="container hero__content">
          <div className="hero__badge animate-fadeInUp">
            <span className="badge-dot" />
            All-in-One Communication Platform
          </div>
          <h1 className="hero__title animate-fadeInUp delay-2">
            Turn Visitors Into
            <span className="hero__title-highlight"> Customers</span>
            <br />With Smart Automation
          </h1>
          <p className="hero__desc animate-fadeInUp delay-3">
            Chatbot + CRM, Email Marketing, WhatsApp Automation, SEO/AEO, Web & App Development — everything your business needs to capture leads and grow faster.
          </p>
          <div className="hero__actions animate-fadeInUp delay-4">
            <Link to="/contact" className="btn-primary"><span>Get Started Free</span></Link>
            <a href="https://mschat.msdiscoveries.com/" target="_blank" rel="noreferrer" className="btn-outline">View Products</a>
          </div>
          <div className="hero__stats animate-fadeInUp delay-5">
            <div className="hero__stat">
              <strong><Counter target={500} suffix="+" /></strong>
              <span>Businesses</span>
            </div>
            <div className="hero__stat-divider" />
            <div className="hero__stat">
              <strong><Counter target={6} /></strong>
              <span>Services</span>
            </div>
            <div className="hero__stat-divider" />
            <div className="hero__stat">
              <strong><Counter target={99} suffix="%" /></strong>
              <span>Uptime</span>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="marquee-strip">
        <div className="marquee-track">
          {[...Array(3)].map((_, i) => (
            <React.Fragment key={i}>
              <span>MS Chat</span><span className="marquee-dot">◆</span>
              <span>Email Campaign</span><span className="marquee-dot">◆</span>
              <span>Email Verifier</span><span className="marquee-dot">◆</span>
              <span>SEO & AEO</span><span className="marquee-dot">◆</span>
              <span>Web Development</span><span className="marquee-dot">◆</span>
              <span>iOS & Android</span><span className="marquee-dot">◆</span>
              <span>Data Services</span><span className="marquee-dot">◆</span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* PRODUCTS */}
      <section className="products-section" ref={productsRef}>
        <div className="container">
          <div className={`products-header ${productsVis ? 'revealed' : ''}`} style={{marginBottom: '40px'}}>
            <div className="section-tag">Our Products</div>
            <h2 className="section-title">Powerful <span className="highlight">Tools</span></h2>
            <p className="section-desc">Each product is designed to help businesses improve communication and convert more customers.</p>
          </div>
          <div className="products-grid">
            {products.map((p, i) => (
              <div
                key={p.id}
                className={`product-card ${productsVis ? 'revealed' : ''}`}
                style={{ animationDelay: `${i * 0.15}s` }}
              >
                <div className="product-card__tag">{p.tagline}</div>
                <h3 className="product-card__name">{p.name}</h3>
                <p className="product-card__desc">{p.desc}</p>
                <a href={p.link} target="_blank" rel="noreferrer" className="product-card__link">
                  Launch {p.name}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </a>
                <div className="product-card__glow" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="services-section" ref={servicesRef}>
        <div className="container">
          <div className={`services-header ${servicesVis ? 'revealed' : ''}`}>
            <div className="section-tag">Our Services</div>
            <h2 className="section-title">We Also <span className="highlight">Provide</span></h2>
            <p className="section-desc">Beyond our products, we offer a full range of digital services.</p>
          </div>
          <div className="services-grid">
            {services.map((s, i) => (
              <div key={i} className={`service-card ${servicesVis ? 'revealed' : ''}`} style={{ animationDelay: `${i * 0.1}s` }}>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="why-section" ref={whyRef}>
        <div className="container">
          <div className={`why-header ${whyVis ? 'revealed' : ''}`}>
            <div className="section-tag">Why Choose Us</div>
            <h2 className="section-title">Why Businesses <span className="highlight">Choose Us</span></h2>
          </div>
          <div className="why-grid">
            {whyItems.map((item, i) => (
              <div key={i} className={`why-card ${whyVis ? 'revealed' : ''}`} style={{ animationDelay: `${i * 0.1}s` }}>
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="stats-section" ref={statsRef}>
        <div className="container">
          <div className="stats-inner">
            {[
              { value: 500, suffix: '+', label: 'Happy Businesses' },
              { value: 1000000, suffix: '+', label: 'Messages Sent' },
              { value: 6, suffix: '', label: 'Services Offered' },
              { value: 24, suffix: '/7', label: 'Support Available' },
            ].map((s, i) => (
              <div key={i} className={`stat-item ${statsVis ? 'revealed' : ''}`} style={{ animationDelay: `${i * 0.15}s` }}>
                <div className="stat-value">
                  {statsVis && <Counter target={s.value} suffix={s.suffix} />}
                </div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO CAN USE */}
      <section className="audience-section" ref={audienceRef}>
        <div className="container">
          <div className={`audience-header ${audienceVis ? 'revealed' : ''}`}>
            <div className="section-tag">Perfect For</div>
            <h2 className="section-title">Who Can Use <span className="highlight">Our Tools</span></h2>
            <p className="section-desc">Our platform is built for businesses of all sizes and industries.</p>
          </div>
          <div className="audience-grid">
            {audiences.map((a, i) => (
              <div key={i} className={`audience-card ${audienceVis ? 'revealed' : ''}`} style={{ animationDelay: `${i * 0.1}s` }}>
                <span>{a.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="home-cta" ref={ctaRef}>
        <div className="container">
          <div className={`home-cta__inner ${ctaVis ? 'revealed' : ''}`}>
            <div className="home-cta__glow" />
            <div className="section-tag" style={{ justifyContent: 'center' }}>Get Started Today</div>
            <h2 className="section-title" style={{ textAlign: 'center' }}>
              Ready to <span className="highlight">Grow Your Business</span> with Smart Automation?
            </h2>
            <p className="section-desc" style={{ textAlign: 'center', margin: '16px auto 32px' }}>
              Join hundreds of businesses already using tools from MS Discoveries.
            </p>
            <div className="home-cta__btns">
              <Link to="/contact" className="btn-primary"><span>Start Free</span></Link>
              <Link to="/contact" className="btn-outline">Contact Us</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
